import { useRef } from 'react'

import Container from '@/components/ui/Container'
import Icon from '@/components/ui/Icon'
import { TONES } from '@/components/ui/tones'
import { useData, useLocale } from '@/hooks/useLocale'
import { useScrollProgress } from '@/hooks/useScrollProgress'
import { LOCALES } from '@/lib/locale'
import { ease, lerp, ramp } from '@/lib/scroll'

// 0 as the section's top enters the bottom of the screen, 1 once it has
// scrolled just past the top.
const progress = (el) =>
  (window.innerHeight - el.getBoundingClientRect().top) /
  (window.innerHeight * 1.05)

// Where each activity card comes to rest, sideways from the column's end edge.
const PILL_OFFSETS = [-90, 0, -40]

/**
 * Plays in as it scrolls into view (design: Home, Statement): the headline
 * fades up, its marked phrases fill with blue one word at a time, then the
 * avatars and text arrive and the activity cards slide in from the side.
 * The headline runs the full page width, wrapping without leaving one word
 * alone on the last line.
 */
export default function StatementSection() {
  const { STATEMENT } = useData()
  const { locale } = useLocale()
  const section = useRef(null)
  const p = useScrollProgress(section, progress)
  // Cards slide in from the end edge: the right in English, the left in Arabic.
  const side = LOCALES[locale].dir === 'rtl' ? -1 : 1
  const lead = ease(ramp(p, 0.45, 0.58))

  // Each marked word takes the next 6% of progress, so a phrase sweeps on.
  let marked = 0
  const headline = STATEMENT.headline.map((part) => {
    if (!part.mark) return part
    const start = 0.12 + marked * 0.06
    marked += part.text.split(' ').length
    return { ...part, fill: ramp(p, start, 0.12 + marked * 0.06) }
  })

  return (
    <Container
      as="section"
      ref={section}
      id="product"
      className="flex flex-col gap-14 pt-16 pb-25"
    >
      <h2
        className="text-[clamp(30px,3.6vw,46px)] leading-[1.18] tracking-[-0.025em] text-pretty"
        style={{ opacity: ease(ramp(p, 0, 0.1)) }}
      >
        {headline.map((part) =>
          part.mark ? (
            <span
              key={part.text}
              className={`-mx-[.14em] rounded-xl bg-[linear-gradient(#e8f0fd,#e8f0fd)] bg-left bg-no-repeat px-[.14em] transition-colors duration-200 rtl:bg-right ${part.fill > 0.5 ? 'text-brand' : part.muted ? 'text-[#a7b1c2]' : ''}`}
              style={{ backgroundSize: `${part.fill * 100}% 100%` }}
            >
              {part.text}
            </span>
          ) : (
            <span key={part.text} className={part.muted ? 'text-[#a7b1c2]' : ''}>
              {part.text}
            </span>
          ),
        )}
      </h2>

      <div className="flex flex-wrap items-center gap-10">
        <div className="flex min-w-65 flex-[1_1_360px] flex-col gap-4">
          <div
            className="flex origin-left rtl:origin-right"
            style={{ opacity: lead, transform: `scale(${lerp(0.9, 1, lead)})` }}
          >
            {STATEMENT.avatars.map((avatar, i) => (
              <span
                key={avatar.initials}
                className={`grid size-11 place-items-center rounded-full text-sm font-semibold ring-3 ring-white ${TONES[avatar.tone]} ${i ? '-ms-2.5' : ''}`}
              >
                {avatar.initials}
              </span>
            ))}
          </div>
          <p
            className="max-w-200 text-[15px]/[1.6] text-pretty text-muted"
            style={{ opacity: lead, transform: `translateY(${(1 - lead) * 16}px)` }}
          >
            {STATEMENT.text}
          </p>
        </div>

        <ul className="flex max-w-155 flex-[1_1_420px] flex-col items-end gap-[18px]">
          {STATEMENT.pills.map((pill, i) => {
            const shown = ease(ramp(p, 0.55 + i * 0.07, 0.68 + i * 0.07))
            const x = ((1 - shown) * 80 + PILL_OFFSETS[i]) * side
            return (
              <li
                key={pill.title}
                className="flex items-center gap-2.5 rounded-2xl bg-white py-2.5 ps-2.5 pe-3.5 shadow-[0_0_0_1px_rgba(20,30,50,.06),0_12px_24px_-16px_rgba(20,30,50,.3)]"
                style={{ opacity: shown, transform: `translateX(${x}px)` }}
              >
                <span
                  className={`grid size-8 shrink-0 place-items-center rounded-[10px] ${TONES[pill.tone]}`}
                >
                  <Icon name={pill.icon} size={15} />
                </span>
                <span className="flex flex-col gap-px whitespace-nowrap">
                  <span className="text-[13px] font-semibold text-[#1e2330]">
                    {pill.title}
                  </span>
                  <span className="text-[11px] text-subtle">{pill.meta}</span>
                </span>
              </li>
            )
          })}
        </ul>
      </div>
    </Container>
  )
}
